/**
 * 權限檢查 Composable
 * 支援模組級別權限控制
 */

import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useMenuStore } from '@/stores/menu.js';
import authService from '@/helpers/authservice/authService';
import { accountHasPermission } from '@/helpers/authservice/permissionCheck.js';

export function usePermissions() {
  const user = computed(() => authService.getCurrentUser());
  const route = useRoute();
  const menuStore = useMenuStore();
  
  /**
   * 獲取當前程式的權限列表
   * @returns {Array<string>} 程式提供的權限列表
   */
  const getCurrentProgramPermissions = () => {
    const currentPath = route.path;
    const formattedMenu = menuStore.formattedMenu;
    
    if (!formattedMenu || formattedMenu.length === 0) {
      return [];
    }
    
    // 移除查詢參數和 hash，只比較路徑部分
    const pathWithoutQuery = currentPath.split('?')[0].split('#')[0];
    
    // 先嘗試完全匹配
    for (const module of formattedMenu) {
      for (const program of module.children || []) {
        if (program.path === pathWithoutQuery) {
          return program.permissions || [];
        }
      }
    }
    
    // 如果完全匹配失敗，嘗試路徑前綴匹配（處理子路由）
    let bestMatch = null;
    let bestMatchLength = 0;
    
    for (const module of formattedMenu) {
      for (const program of module.children || []) {
        if (program.path && pathWithoutQuery.startsWith(program.path)) {
          // 確保是完整路徑匹配（不是部分匹配）
          const pathEnd = pathWithoutQuery[program.path.length];
          if (!pathEnd || pathEnd === '/') {
            if (program.path.length > bestMatchLength) {
              bestMatch = program.permissions || [];
              bestMatchLength = program.path.length;
            }
          }
        }
      }
    }
    
    return bestMatch || [];
  };
  
  /**
   * 檢查是否有特定權限
   * @param {string} permission - 權限代碼，例如 'member:read'
   * @param {boolean} checkProgramPermissions - 是否檢查程式是否提供該功能（預設 true）
   * @returns {boolean}
   */
  const hasPermission = (permission, checkProgramPermissions = true) => {
    if (!user.value) return false;

    const userHasPermission = accountHasPermission(user.value, permission);
    
    if (!userHasPermission) {
      return false;
    }
    
    // 如果不需要檢查程式權限，直接返回 true
    if (!checkProgramPermissions) {
      return true;
    }
    
    // 檢查程式是否提供該功能
    const programPermissions = getCurrentProgramPermissions();
    
    // 如果程式沒有設定權限（空陣列），則允許所有功能（向後相容）
    if (programPermissions.length === 0) {
      return true;
    }
    
    // 檢查程式是否提供該權限
    return programPermissions.includes(permission);
  };
  
  /**
   * 檢查是否有任意一個權限
   * @param {Array<string>} permissions - 權限代碼陣列
   * @returns {boolean}
   */
  const hasAnyPermission = (permissions) => {
    if (!permissions || permissions.length === 0) return true;
    return permissions.some(p => hasPermission(p));
  };
  
  /**
   * 檢查是否有所有權限
   * @param {Array<string>} permissions - 權限代碼陣列
   * @returns {boolean}
   */
  const hasAllPermissions = (permissions) => {
    if (!permissions || permissions.length === 0) return true;
    return permissions.every(p => hasPermission(p));
  };
  
  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  };
}
